import React, {useEffect, useMemo, useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import {
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CloseIcon from "@material-ui/icons/Close";
import SmsIcon from "@material-ui/icons/Sms";
import {useSelector} from "react-redux";
import {useSmsTwoFactor} from "store/two-factor/sms.store";
import {useTwoFactorSettings} from "store/two-factor/two-factor-settings.store";
import {useCountries} from "store/countries/countries.store";
import {selectUser} from "store/auth/selectors";
import {formatCountdown} from "tools/formats/formatCountdown";
import {formatISODate} from "tools/formats/formatDate";
import {maskPhone} from "tools/formats/maskPhone";
import {getErrorMessage, getRetryAfterSeconds} from "tools/error";
import useDateFnsLocale from "hooks/useDateFnsLocale";
import ConfirmPasswordModal from "view/components/ConfirmPasswordModal";
import Alert from "view/components/Alert";
import CodeInput from "view/components/CodeInput";
import RecoveryCodes from "view/components/RecoveryCodes";
import PhoneNumberInput from "view/components/PhoneNumberInput";
import useStyles from "./styles";

type Props = {
  onBack: () => void;
};

const SMS_DELIVERY_HINT_DELAY_MS = 20_000;

const INVALID_NUMBER_ERROR =
  "Enter a valid phone number, including your country code.";

const SmsTwoFactorManager: React.FC<Props> = ({onBack}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const locale = useDateFnsLocale();

  const status = useSmsTwoFactor((state) => state.status);
  const pendingPhone = useSmsTwoFactor((state) => state.pendingPhone);
  const recoveryCodes = useSmsTwoFactor((state) => state.recoveryCodes);
  const fetchStatus = useSmsTwoFactor((state) => state.fetchStatus);
  const beginSetup = useSmsTwoFactor((state) => state.beginSetup);
  const verifySetup = useSmsTwoFactor((state) => state.verifySetup);
  const revoke = useSmsTwoFactor((state) => state.revoke);
  const clearPendingPhone = useSmsTwoFactor((state) => state.clearPendingPhone);
  const clearRecoveryCodes = useSmsTwoFactor(
    (state) => state.clearRecoveryCodes
  );
  const confirmPassword = useTwoFactorSettings(
    (state) => state.confirmPassword
  );
  const countries = useCountries((state) => state.countries);
  const fetchCountries = useCountries((state) => state.fetchCountries);

  const {hasPassword} = useSelector(selectUser);

  const [isConfirmingPassword, setIsConfirmingPassword] = useState(false);
  const [passwordlessSetup, setPasswordlessSetup] = useState(false);
  const [confirmedPassword, setConfirmedPassword] = useState<string | null>(
    null
  );
  const [isRevoking, setIsRevoking] = useState(false);
  const [phone, setPhone] = useState("");
  const [setupError, setSetupError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<
    string | null
  >(null);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [code, setCode] = useState("");
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showDeliveryHint, setShowDeliveryHint] = useState(false);
  const [lastSendParams, setLastSendParams] = useState<{
    password?: string;
    phone: string;
  } | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState(false);

  useEffect(() => {
    fetchStatus();
    return () => {
      clearPendingPhone();
      clearRecoveryCodes();
    };
  }, [fetchStatus, clearPendingPhone, clearRecoveryCodes]);

  // Fetched on mount rather than when the phone step opens: password
  // confirmation sits between the two, which is more than enough time for the
  // list to land, so the select never renders all 250 countries and then blinks
  // down to the allowed ones.
  //
  // Deliberately NOT `fetchCountries(true)`. That flag skips the request
  // whenever the store already holds countries, and the store is shared with
  // address forms and show settings that populate it for their own reasons —
  // which meant an admin who enabled a country and walked to this page in the
  // same session kept seeing the list as it was when some unrelated screen
  // happened to load it. `getMany` is cached in Redis on the server and
  // invalidated on every toggle, so asking again is cheap and always current.
  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  /**
   * The countries SMS can actually reach. The backend refuses everything else
   * with `SMS_TWO_FACTOR_REGION_NOT_SUPPORTED`, so offering the full ISO list
   * only let people pick a number that was never going to work.
   */
  const smsCountries = useMemo(
    () =>
      countries.filter(({smsEnabled}) => smsEnabled).map(({code2}) => code2),
    [countries]
  );

  useEffect(() => {
    if (!pendingPhone) {
      setShowDeliveryHint(false);
      return undefined;
    }

    setShowDeliveryHint(false);
    const timeout = setTimeout(() => {
      setShowDeliveryHint(true);
    }, SMS_DELIVERY_HINT_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [pendingPhone]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;

    const timeout = setTimeout(() => {
      setResendCooldown((seconds) => seconds - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [resendCooldown]);

  const resetPhoneEntry = () => {
    setConfirmedPassword(null);
    setPasswordlessSetup(false);
    setPhone("");
    setSetupError(null);
  };

  const handleConfirmPassword = async (password: string) => {
    setConfirmPasswordError(null);
    await confirmPassword(password);
    setConfirmedPassword(password);
    setIsConfirmingPassword(false);
  };

  const handleSendCode = async () => {
    if (!confirmedPassword && !passwordlessSetup) return;

    if (!phone || inputError) {
      setInputError(INVALID_NUMBER_ERROR);
      return;
    }

    setSetupError(null);
    setIsSendingCode(true);
    try {
      const resendAfter = await beginSetup(
        confirmedPassword ?? undefined,
        phone
      );
      setLastSendParams({password: confirmedPassword ?? undefined, phone});
      setResendCooldown(resendAfter);
      setConfirmedPassword(null);
      setPasswordlessSetup(false);
      setPhone("");
    } catch (e) {
      const status = (e as {response?: {status?: number}})?.response?.status;
      if (status === 401) {
        setConfirmedPassword(null);
        setPhone("");
        setConfirmPasswordError(t("common.two_factor.incorrect_password"));
        setIsConfirmingPassword(true);
      } else {
        // A rate-limit rejection carries the remaining wait, so the countdown
        // recovers even when this send never happened.
        const retryAfter = getRetryAfterSeconds(e);
        if (retryAfter) setResendCooldown(retryAfter);
        // Coded BE errors (an unsupported SMS region, validation) resolve to
        // actionable copy instead of a blanket "something went wrong" — the
        // user has to know the number itself is the problem.
        setSetupError(getErrorMessage(e));
      }
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerify = async (submittedCode: string = code, silent = false) => {
    setVerifyError(null);
    setIsVerifying(true);
    try {
      await verifySetup(submittedCode);
      setJustAdded(true);
    } catch (e) {
      if (silent) return;
      const status = (e as {response?: {status?: number}})?.response?.status;
      const errorCode = (e as {response?: {data?: {message?: string}}})
        ?.response?.data?.message;
      if (status === 429) {
        setVerifyError(t("common.two_factor.too_many_attempts"));
      } else if (errorCode?.endsWith("_NOT_STARTED")) {
        setVerifyError(t("common.two_factor.code_expired"));
      } else {
        setVerifyError(t("common.two_factor.incorrect_code"));
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!lastSendParams || resendCooldown > 0) return;

    setResendError(null);
    setIsResending(true);
    try {
      setResendCooldown(
        await beginSetup(lastSendParams.password, lastSendParams.phone)
      );
    } catch (e) {
      const retryAfter = getRetryAfterSeconds(e);
      if (retryAfter) setResendCooldown(retryAfter);
      setResendError(getErrorMessage(e));
    } finally {
      setIsResending(false);
    }
  };

  const handleConfirmRevoke = async (password?: string) => {
    await revoke(password);
    setIsRevoking(false);
  };

  const handlePhoneChange = (e164: string, isValid: boolean) => {
    setPhone(e164);
    setInputError(!e164 || isValid ? null : INVALID_NUMBER_ERROR);
  };

  const renderPhoneEntry = () => (
    <div className={classes.step}>
      <Typography className={classes.helper}>
        <T keyName="common.two_factor.sms.enter_phone" />
      </Typography>
      <PhoneNumberInput
        label={t("common.two_factor.sms.phone_label")}
        value={phone}
        onChange={handlePhoneChange}
        disabled={isSendingCode}
        error={!!inputError}
        helperText={inputError ?? undefined}
        allowedCountries={smsCountries}
        autoFocus
      />
      {setupError && (
        <div className={classes.alertWrapper}>
          <Alert severity="error" fullWidth>
            {setupError}
          </Alert>
        </div>
      )}
      <div className={classes.actionsRow}>
        <Button
          color="primary"
          disabled={isSendingCode}
          onClick={resetPhoneEntry}
        >
          <T keyName="common.action.cancel" />
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={isSendingCode || !phone || !!inputError}
          endIcon={
            isSendingCode ? (
              <CircularProgress size={18} color="inherit" />
            ) : undefined
          }
          onClick={handleSendCode}
        >
          <T keyName="common.two_factor.send_code" />
        </Button>
      </div>
    </div>
  );

  const renderVerifyCode = () => {
    return (
      <div className={classes.step}>
        <Typography className={classes.helper}>
          <T
            keyName="common.two_factor.sms.enter_code"
            params={{phone: pendingPhone}}
          />
        </Typography>
        <CodeInput
          value={code}
          onChange={setCode}
          disabled={isVerifying}
          autoFocus
          onComplete={handleVerify}
        />
        {verifyError && (
          <div className={classes.alertWrapper}>
            <Alert severity="error" fullWidth>
              {verifyError}
            </Alert>
          </div>
        )}
        {resendError && (
          <div className={classes.alertWrapper}>
            <Alert severity="error" fullWidth>
              {resendError}
            </Alert>
          </div>
        )}
        {showDeliveryHint && !verifyError && !resendError && (
          <div className={classes.alertWrapper}>
            <Alert severity="info" fullWidth>
              <T keyName="common.two_factor.sms.delivery_hint" />
            </Alert>
          </div>
        )}
        <div className={classes.buttonWrapper}>
          <Button
            color="primary"
            disabled={isResending || resendCooldown > 0 || !lastSendParams}
            endIcon={
              isResending ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
            onClick={handleResendCode}
          >
            {resendCooldown > 0
              ? `Resend SMS (${formatCountdown(resendCooldown)})`
              : "Resend SMS"}
          </Button>
        </div>
        <div className={classes.actionsRow}>
          <Button
            color="primary"
            disabled={isVerifying}
            onClick={() => {
              clearPendingPhone();
              setCode("");
              setVerifyError(null);
              setLastSendParams(null);
              setResendError(null);
              setResendCooldown(0);
            }}
          >
            <T keyName="common.action.cancel" />
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isVerifying || code.length < 6}
            endIcon={
              isVerifying ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
            onClick={() => handleVerify()}
          >
            <T keyName="common.action.verify" />
          </Button>
        </div>
      </div>
    );
  };

  const renderManage = () => (
    <>
      {status?.configured ? (
        <div className={classes.rowCard}>
          <div className={classes.iconBox}>
            <SmsIcon />
          </div>
          <div className={classes.rowContent}>
            <Typography className={classes.rowTitle}>
              {status.phone ? maskPhone(status.phone) : ""}
            </Typography>
            <Typography className={classes.rowSubtitle}>
              {status.verifiedAt
                ? t("common.two_factor.enabled_on", {
                    date: formatISODate(
                      status.verifiedAt,
                      "d MMMM yyyy",
                      locale
                    ),
                  })
                : t("common.two_factor.enabled")}
            </Typography>
          </div>
          <IconButton
            size="small"
            onClick={() => {
              if (hasPassword) {
                setIsRevoking(true);
              } else {
                handleConfirmRevoke();
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
      ) : (
        status !== null && (
          <Typography variant="body2" className={classes.emptyText}>
            <T keyName="common.two_factor.sms.description" />
          </Typography>
        )
      )}
      <div className={classes.buttonWrapper}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            setPhone(status?.configured && status.phone ? status.phone : "");
            setInputError(null);
            if (hasPassword) {
              setIsConfirmingPassword(true);
            } else {
              setPasswordlessSetup(true);
            }
          }}
        >
          {status?.configured
            ? t("common.two_factor.sms.change")
            : t("common.two_factor.sms.setup")}
        </Button>
      </div>
    </>
  );

  return (
    <>
      <div className={classes.backRow}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography className={classes.backTitle}>
          {recoveryCodes ? (
            <T keyName="common.two_factor.recovery.save_title" />
          ) : (
            <T keyName="common.two_factor.method.sms" />
          )}
        </Typography>
      </div>

      {justAdded && (
        <div
          className={
            recoveryCodes
              ? `${classes.alertWrapper} ${classes.alertInset}`
              : classes.alertWrapper
          }
        >
          <Alert severity="success" fullWidth>
            Phone number added as a 2-Step Verification method
          </Alert>
        </div>
      )}

      {recoveryCodes ? (
        <RecoveryCodes codes={recoveryCodes} onDone={clearRecoveryCodes} />
      ) : pendingPhone ? (
        renderVerifyCode()
      ) : confirmedPassword || passwordlessSetup ? (
        renderPhoneEntry()
      ) : (
        renderManage()
      )}

      {isConfirmingPassword && (
        <ConfirmPasswordModal
          subtitle={t("common.two_factor.method.sms")}
          description={
            confirmPasswordError ? (
              <Alert severity="error" fullWidth>
                {confirmPasswordError}
              </Alert>
            ) : (
              t("common.two_factor.sms.setup_prompt")
            )
          }
          submitTitle={t("common.action.continue")}
          onClose={() => {
            setIsConfirmingPassword(false);
            setConfirmPasswordError(null);
          }}
          onConfirm={handleConfirmPassword}
        />
      )}

      {isRevoking && (
        <ConfirmPasswordModal
          subtitle={t("common.two_factor.method.sms")}
          description={t("common.two_factor.sms.remove_prompt")}
          submitTitle={t("common.two_factor.revoke")}
          onClose={() => setIsRevoking(false)}
          onConfirm={handleConfirmRevoke}
        />
      )}
    </>
  );
};

export default SmsTwoFactorManager;
